import { useRef, useState } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { AirQueryParams } from "../query.types";
import { AirResponseDTO } from "../response.types";
import { PaymentsModalType, SubscriptionDataModel } from "entities/shared/subscriptionModel";
import { OnErrorCallback, OnSettledCallback, OnSuccessCallback, useRequestCallbacks } from "shared/api/lib/api.hooks";
import { useAppDispatch } from "shared/lib/redux";
import { PartialBy } from "shared/lib/typeCheck.lib";

import { AirSearchCardModel } from "../../model/model";
import { airSearchService } from "../api";

type RequestLimitReachedCallbackFunc = () => void;
type AirSearchCacheFunc = (data: AirResponseDTO | undefined) => void;
type ResetSearchQueryFunc = () => Promise<void>;
type OnSuccessCallbackFunc = OnSuccessCallback<AirSearchCardModel>;

type SearchRequestFuncOptions = {
  onSuccess?: OnSuccessCallbackFunc;
  onError?: OnErrorCallback<SearchError>;
  onSettled?: OnSettledCallback<AirSearchCardModel, SearchError>;
  resetQueryBeforeRequest?: boolean;
  onAppIntegrationRequestLimitReached?: RequestLimitReachedCallbackFunc;
  onWebAppRequestLimitReached?: RequestLimitReachedCallbackFunc;
};

export type AirSearchRequestFunc = (params: AirQueryParams, options?: SearchRequestFuncOptions) => void;

export type UseSearchQueryReturnType = {
  searchRequest: AirSearchRequestFunc;
  resetSearchQuery: ResetSearchQueryFunc;
  removeSearchCache: () => void;
  setSearchCache: AirSearchCacheFunc;
  cancelSearchRequest: () => Promise<void>;
  searchResponse: AirSearchCardModel | undefined;
  isSearchSuccess: boolean;
  isSearchError: boolean;
  isSearchLoading: boolean;
  isSearchFetching: boolean;
};

type SearchError = Error;
type QueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey"> & {
  isAuthenticated: boolean;
  setSubscription: (subscription: SubscriptionDataModel) => {
    type: string;
    payload: SubscriptionDataModel | null;
  };
  setActivePaymentModal: (activePaymentModal: PaymentsModalType) => {
    type: string;
    payload: PaymentsModalType | null;
  };
  setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached: boolean) => {
    type: string;
    payload: boolean | null;
  };
  setWebAppRequestLimitReached: (webAppRequestLimitReached: boolean) => {
    type: string;
    payload: boolean | null;
  };
};

const AIR_SEARCH_CACHE_KEY = "search-air-tracking-query";

export const useSearchQuery = ({
  queryKey = [AIR_SEARCH_CACHE_KEY],
  isAuthenticated,
  setSubscription,
  setActivePaymentModal,
  setAppIntegrationRequestLimitReached,
  setWebAppRequestLimitReached,
}: QueryParams): UseSearchQueryReturnType => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useState<AirQueryParams>();
  const [enabled, setEnabled] = useState(false);

  const appIntegrationRequestLimitReachedCallbackRef = useRef<RequestLimitReachedCallbackFunc | undefined>();
  const webAppRequestLimitReachedCallbackRef = useRef<RequestLimitReachedCallbackFunc | undefined>();

  const meta = useQuery<AirResponseDTO, SearchError, AirSearchCardModel>({
    queryKey,
    queryFn: searchParams ? ({ signal }) => airSearchService.search(searchParams, signal) : undefined,
    select: (data) => new AirSearchCardModel(data),
    enabled,
  });

  const { data, isError, isLoading, isSuccess, isFetching } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<AirSearchCardModel, SearchError>(meta);

  const removeSearchCache = () => {
    queryClient.removeQueries({ queryKey });
  };

  const resetSearchQuery: ResetSearchQueryFunc = () => {
    return queryClient.resetQueries({ queryKey });
  };

  const setSearchCache: AirSearchCacheFunc = (data) => {
    queryClient.setQueryData(queryKey, data);
  };

  const searchRequest: AirSearchRequestFunc = (
    params,
    {
      onSuccess,
      onError,
      onSettled,
      resetQueryBeforeRequest = true,
      onAppIntegrationRequestLimitReached,
      onWebAppRequestLimitReached,
    } = {}
  ) => {
    setRequestCallbacks({
      onSuccess,
      onError,
      onSettled: (response, error) => {
        // TODO: Implement DataLayersManager
        // const dlm = new DataLayersManager();
        const subscription = response?.subscription;
        if (subscription) {
          dispatch(setSubscription(subscription));
        }

        if (response?.requestLimitReachedAppIntegration) {
          dispatch(setAppIntegrationRequestLimitReached(true));
          appIntegrationRequestLimitReachedCallbackRef.current?.();
        }
        if (response?.requestLimitReachedWebApp) {
          if (isAuthenticated) {
            if (subscription?.isFree) {
              dispatch(setActivePaymentModal(PaymentsModalType.FREE));
              // dlm.setModel(response).searchLimitReached();
            } else {
              dispatch(setActivePaymentModal(PaymentsModalType.PAY));
            }
          } else {
            dispatch(setWebAppRequestLimitReached(true));
          }
          webAppRequestLimitReachedCallbackRef.current?.();
        }
        onSettled?.(response, error);
        setEnabled(false);
      },
    });

    appIntegrationRequestLimitReachedCallbackRef.current = onAppIntegrationRequestLimitReached;
    webAppRequestLimitReachedCallbackRef.current = onWebAppRequestLimitReached;

    if (resetQueryBeforeRequest) {
      resetSearchQuery().finally(() => {
        setSearchParams(params);
        setEnabled(true);
      });
      return;
    }

    setSearchParams(params);
    setEnabled(true);
  };

  const cancelSearchRequest = () => {
    return queryClient.cancelQueries({ queryKey });
  };

  return {
    searchRequest: searchRequest,
    resetSearchQuery: resetSearchQuery,
    removeSearchCache: removeSearchCache,
    setSearchCache: setSearchCache,
    searchResponse: data,
    isSearchSuccess: isSuccess,
    isSearchError: isError,
    isSearchLoading: isLoading,
    isSearchFetching: isFetching,
    cancelSearchRequest: cancelSearchRequest,
  };
};
